import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { API, getBackendBaseUrl } from '@/lib/api';
import { openExternalUrl } from '@/lib/nativeNavigation';
import { Button } from '@/components/ui/button';
import {
  MessageSquare,
  Zap,
  Clock,
  Shield,
  ChevronRight,
  Check,
  Target,
  Brain,
  Filter,
  Sparkles,
  BriefcaseBusiness,
  HeartHandshake
} from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { toast } from 'sonner';

const BrandIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M5 4h14v4.2L8.7 20H5.2l9.1-10.4H5V4z" />
  </svg>
);

const TRACKING_API = `${getBackendBaseUrl()}/api/public/analytics/track`;

const getOrCreateStorageId = (key, storage) => {
  try {
    const current = storage.getItem(key);
    if (current) return current;
    const created = `${key}_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
    storage.setItem(key, created);
    return created;
  } catch {
    return `${key}_${Date.now().toString(36)}`;
  }
};

const detectClientEnvironment = () => {
  const width = window.innerWidth || 0;
  let deviceType = 'desktop';
  if (width <= 768) {
    deviceType = 'mobile';
  } else if (width <= 1024) {
    deviceType = 'tablet';
  }

  const ua = navigator.userAgent || '';
  let browser = 'Outro';
  if (/Edg/i.test(ua)) browser = 'Edge';
  else if (/OPR|Opera/i.test(ua)) browser = 'Opera';
  else if (/Chrome/i.test(ua)) browser = 'Chrome';
  else if (/Firefox/i.test(ua)) browser = 'Firefox';
  else if (/Safari/i.test(ua)) browser = 'Safari';

  let os = 'Outro';
  if (/Android/i.test(ua)) os = 'Android';
  else if (/iPhone|iPad|iPod/i.test(ua)) os = 'iOS';
  else if (/Windows/i.test(ua)) os = 'Windows';
  else if (/Mac OS X|Macintosh/i.test(ua)) os = 'macOS';
  else if (/Linux/i.test(ua)) os = 'Linux';

  return {
    deviceType,
    deviceLabel: `${deviceType} ${width ? `${width}px` : ''}`.trim(),
    browser,
    os,
    viewportWidth: width,
    viewportHeight: window.innerHeight || 0,
  };
};

const FloatingParticles = ({ accentIcon: AccentIcon = MessageSquare }) => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    {[...Array(6)].map((_, i) => (
      <div
        key={i}
        className="floating-particle absolute rounded-full opacity-20"
        style={{
          width: `${20 + Math.random() * 40}px`,
          height: `${20 + Math.random() * 40}px`,
          left: `${10 + i * 15}%`,
          top: `${20 + Math.random() * 60}%`,
          background: `linear-gradient(135deg, #0EA5E9, #7C3AED)`,
          animationDelay: `${i * 0.5}s`,
          animationDuration: `${6 + Math.random() * 4}s`
        }}
      />
    ))}
    {[...Array(4)].map((_, i) => (
      <div
        key={`msg-${i}`}
        className="floating-message absolute opacity-10"
        style={{
          left: `${5 + i * 25}%`,
          top: `${30 + Math.random() * 40}%`,
          animationDelay: `${i * 0.8}s`,
          animationDuration: `${8 + Math.random() * 4}s`
        }}
      >
        <AccentIcon className="w-8 h-8 text-emerald-500" />
      </div>
    ))}
  </div>
);

const AnimatedCounter = ({ end, duration = 2000, suffix = '' }) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let startTime;
    let animationFrame;

    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setCount(Math.floor(progress * end));

      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [end, duration]);

  return <span>{count}{suffix}</span>;
};

const InteractiveDemo = ({ title, subtitle, tabs, content }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    if (!isHovered) {
      const interval = setInterval(() => {
        setActiveTab((prev) => (prev + 1) % tabs.length);
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [isHovered, tabs.length]);

  const getTypeStyles = (type) => {
    const styles = {
      info: 'bg-blue-50 border-blue-200 text-blue-700',
      question: 'bg-amber-50 border-amber-200 text-amber-700',
      success: 'bg-emerald-50 border-emerald-200 text-emerald-700',
      urgent: 'bg-orange-50 border-orange-200 text-orange-700',
      suggestion: 'bg-violet-50 border-violet-200 text-violet-700'
    };
    return styles[type] || styles.info;
  };

  return (
    <div
      className="bg-white rounded-2xl shadow-[0_20px_60px_-15px_rgba(0,0,0,0.15)] border border-zinc-200 overflow-hidden transition-transform duration-300 hover:scale-[1.02]"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="flex border-b border-zinc-100">
        {tabs.map((tab, i) => (
          <button
            key={i}
            onClick={() => setActiveTab(i)}
            className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 text-sm font-medium transition-all ${
              activeTab === i
                ? 'bg-sky-50 text-sky-700 border-b-2 border-sky-500'
                : 'text-zinc-500 hover:bg-zinc-50'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            <span className="hidden sm:inline">{tab.label}</span>
          </button>
        ))}
      </div>

      <div className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
            <BrandIcon className="w-5 h-5 text-sky-600" />
          </div>
          <div>
            <h4 className="font-heading font-semibold text-zinc-900">{title}</h4>
            <p className="text-xs text-zinc-500">{subtitle}</p>
          </div>
        </div>

        <div className="space-y-3">
          {content[activeTab].items.map((item, i) => (
            <div
              key={i}
              className={`p-3 rounded-lg border transition-all duration-300 ${getTypeStyles(item.type)}`}
              style={{
                animationDelay: `${i * 100}ms`,
                animation: 'fadeInUp 0.4s ease forwards'
              }}
            >
              <p className="text-sm font-medium">{item.text}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const LANDING_VARIANTS = {
  personal: {
    routeLabel: 'Pessoal',
    switchLabel: 'Versão Business',
    switchHref: '/business',
    heroBadge: 'Resumos Inteligentes de Grupos de Conversa',
    heroTitleStart: 'Pare de perder tempo com',
    heroTitleHighlight: 'dezenas de grupos',
    heroDescription: 'Compartilhe ou importe conversas no Zapt Resumo e receba resumos acionáveis com IA.',
    heroStrong: 'Saiba o que aconteceu, onde precisa responder e o que pode ignorar.',
    demoTitle: 'Resumo pessoal do dia',
    demoSubtitle: 'Grupo: Família • Hoje',
    demoTabs: [
      { label: 'Resumo', icon: Brain },
      { label: 'Urgentes', icon: Zap },
      { label: 'Sugestões', icon: Sparkles }
    ],
    demoContent: [
      {
        items: [
          { text: 'Combinaram o aniversário de domingo e o local já foi definido.', type: 'info' },
          { text: 'Perguntaram se você consegue levar as bebidas.', type: 'question' },
          { text: 'A escola confirmou a reunião de pais.', type: 'success' }
        ]
      },
      {
        items: [
          { text: '@você - Pode confirmar sua presença hoje?', type: 'urgent' },
          { text: '@você - Consegue responder sobre a carona?', type: 'urgent' },
          { text: '@você - Vai levar algo para o encontro?', type: 'urgent' }
        ]
      },
      {
        items: [
          { text: 'Consigo sim, confirmo ainda hoje.', type: 'suggestion' },
          { text: 'Posso levar as bebidas e chegar às 19h.', type: 'suggestion' },
          { text: 'Vou verificar e te respondo em seguida.', type: 'suggestion' }
        ]
      }
    ],
    stats: [
      { end: 500, suffix: '+', label: 'Usuários ativos' },
      { end: 50, suffix: 'k', label: 'Mensagens resumidas' },
      { end: 2, suffix: 'h', label: 'Economizadas/dia' },
    ],
    howItWorks: [
      {
        icon: Filter,
        step: '01',
        title: 'Compartilhe ou Importe',
        description: 'Suba exportações de conversas, cole mensagens ou use o modo Sandbox para testar o fluxo.'
      },
      {
        icon: Brain,
        step: '02',
        title: 'IA Organiza',
        description: 'A IA resume o contexto, separa pendências e destaca onde você foi acionado.'
      },
      {
        icon: Target,
        step: '03',
        title: 'Você Decide Rápido',
        description: 'Veja prioridades, copie respostas sugeridas e economize tempo no que realmente importa.'
      }
    ],
    benefitsTitle: 'Recupere o controle das suas conversas',
    benefitsDescription: 'O problema nao e apenas ler tudo. E saber o que aconteceu, o que exige resposta e onde voce precisa agir. O Zapt Resumo transforma esse caos em clareza.',
    benefits: [
      { icon: Clock, title: 'Economize Tempo', desc: 'Entenda tudo em 3 minutos', color: 'blue' },
      { icon: Target, title: 'Prioridade Clara', desc: 'Veja o que importa em cada grupo', color: 'amber' },
      { icon: Zap, title: 'Respostas Prontas', desc: 'Copie respostas sugeridas com contexto', color: 'violet' },
      { icon: Shield, title: 'Controle Total', desc: 'Você escolhe o que compartilhar e quando', color: 'emerald' },
    ],
    plans: {
      starterDescription: 'Para começar com clareza e controle',
      proDescription: 'Feito para Você',
      businessDescription: 'Para mais usuários e uso avançado'
    },
    faq: [
      {
        q: 'O Zapt Resumo acessa minhas conversas automaticamente?',
        a: 'Não fazemos varredura automática invisível. O produto foi desenhado para funcionar com conversas compartilhadas ou importadas por você, com segurança e previsibilidade.'
      },
      {
        q: 'O que é o modo Sandbox?',
        a: 'É um ambiente de testes com dados simulados. Você pode experimentar as funcionalidades do Zapt Resumo sem importar nenhuma conversa real.'
      },
      {
        q: 'Isso pode virar app para iPhone e Android?',
        a: 'Sim. O caminho mais viável é manter o painel web como base e evoluir para um app com foco em leitura de resumos, prioridades e compartilhamento de exportações direto do celular.'
      },
      {
        q: 'Meus dados são seguros?',
        a: 'Sim. Você controla totalmente seus dados. Pode apagar tudo a qualquer momento. Não compartilhamos dados com terceiros e seguimos a LGPD.'
      },
      {
        q: 'Como funciona a IA de resumos?',
        a: 'A IA analisa as mensagens importadas, identifica perguntas, decisões e pendências, e gera resumos inteligentes com sugestões de respostas.'
      },
      {
        q: 'Posso cancelar a qualquer momento?',
        a: 'Sim. Você pode cancelar sua assinatura a qualquer momento sem taxas adicionais.'
      }
    ],
    finalTitle: 'Pronto para transformar caos em clareza?',
    finalDescription: 'Experimente o Zapt Resumo por 7 dias com cartão cadastrado e veja como ficar por dentro, responder melhor e ganhar tempo no dia a dia.',
    finalButton: 'Iniciar teste de 7 dias',
    securityDescription: 'O Zapt Resumo foi desenhado para funcionar com conversas compartilhadas ou importadas por você. Sem varredura invisível. Sem promessas frágeis. Seus dados são seus.',
    accentIcon: HeartHandshake
  },
  business: {
    routeLabel: 'Business',
    switchLabel: 'Versão Pessoal',
    switchHref: '/pessoal',
    heroBadge: 'IA para Comunicação e Produtividade de PMEs',
    heroTitleStart: 'Sua operação não pode travar em',
    heroTitleHighlight: 'grupos e mensagens soltas',
    heroDescription: 'Centralize conversas de clientes, equipes e operação no Zapt Resumo Business com IA.',
    heroStrong: 'Descubra o que exige resposta, o que virou demanda e onde há oportunidade comercial.',
    demoTitle: 'Painel de operação com IA',
    demoSubtitle: 'Equipe comercial • Hoje',
    demoTabs: [
      { label: 'Contexto', icon: Brain },
      { label: 'Demandas', icon: Target },
      { label: 'Oportunidades', icon: BriefcaseBusiness }
    ],
    demoContent: [
      {
        items: [
          { text: 'Cliente confirmou visita e pediu proposta ajustada.', type: 'info' },
          { text: 'Equipe alinhou entrega para amanhã às 10h.', type: 'success' },
          { text: 'Financeiro cobrou retorno sobre contrato em aberto.', type: 'question' }
        ]
      },
      {
        items: [
          { text: 'Responder proposta com urgência até o fim do dia.', type: 'urgent' },
          { text: 'Definir responsável pela entrega ao cliente.', type: 'urgent' },
          { text: 'Confirmar prazo comercial com o financeiro.', type: 'urgent' }
        ]
      },
      {
        items: [
          { text: 'Há chance de upsell no pacote premium citado hoje.', type: 'suggestion' },
          { text: 'O cliente demonstrou interesse em novo projeto para maio.', type: 'suggestion' },
          { text: 'Existe gancho para retomar negociação parada da semana passada.', type: 'suggestion' }
        ]
      }
    ],
    stats: [
      { end: 120, suffix: '+', label: 'PMEs em validação' },
      { end: 18, suffix: 'k', label: 'Interações operacionais tratadas' },
      { end: 4, suffix: 'h', label: 'Ganhas por equipe/semana' },
    ],
    howItWorks: [
      {
        icon: Filter,
        step: '01',
        title: 'Conecte a rotina da operação',
        description: 'Importe grupos de clientes, atendimento, comercial e equipe para organizar a comunicação de forma centralizada.'
      },
      {
        icon: Brain,
        step: '02',
        title: 'IA lê contexto e impacto',
        description: 'A IA identifica demandas, urgências, follow-ups, oportunidades de negócio e riscos operacionais.'
      },
      {
        icon: Target,
        step: '03',
        title: 'Time responde com prioridade',
        description: 'Acompanhe o que virou ação, distribua respostas com contexto e reduza perda de timing comercial.'
      }
    ],
    benefitsTitle: 'Uma camada de inteligência para pequenas equipes',
    benefitsDescription: 'Para PMEs, o problema não é só excesso de mensagens. É perder contexto comercial, atrasar resposta, ignorar demandas críticas e deixar dinheiro na mesa.',
    benefits: [
      { icon: Clock, title: 'Mais produtividade', desc: 'Menos tempo lendo grupo por grupo', color: 'blue' },
      { icon: Target, title: 'Demandas claras', desc: 'Veja o que precisa de ação imediata', color: 'amber' },
      { icon: BriefcaseBusiness, title: 'Mais oportunidades', desc: 'Enxergue sinais comerciais na conversa', color: 'violet' },
      { icon: Shield, title: 'Operação organizada', desc: 'Centralize contexto sem depender de memória', color: 'emerald' },
    ],
    plans: {
      starterDescription: 'Para validar a operação com até 2 grupos',
      proDescription: 'Ideal para PMEs em crescimento',
      businessDescription: 'Para times e operação'
    },
    faq: [
      {
        q: 'Esse produto serve para pequenas empresas?',
        a: 'Sim. A versão Business foi pensada para PMEs que lidam com muitos grupos de atendimento, comercial, operação e clientes.'
      },
      {
        q: 'A IA identifica oportunidades de negócio?',
        a: 'Sim. O sistema pode destacar conversas com sinais de follow-up, upsell, urgência comercial e risco de atraso em resposta.'
      },
      {
        q: 'Posso usar com minha equipe?',
        a: 'Sim. Os planos pagos suportam mais grupos, histórico maior e espaço para evoluir o uso com membros e gestão interna.'
      },
      {
        q: 'É uma ferramenta oficial do WhatsApp?',
        a: 'Não. O Zapt Resumo é um produto independente da Openova e trabalha com conversas compartilhadas ou importadas pelo usuário.'
      },
      {
        q: 'Como isso ajuda uma PME no dia a dia?',
        a: 'Reduz perda de timing, melhora follow-up, organiza contexto e acelera resposta em conversas que hoje ficam espalhadas em grupos.'
      },
      {
        q: 'Posso começar pequeno e depois subir de plano?',
        a: 'Sim. Você pode validar com poucos grupos e evoluir conforme o time e o uso crescerem.'
      }
    ],
    finalTitle: 'Pronto para transformar comunicação em produtividade?',
    finalDescription: 'Inicie 7 dias de teste no Zapt Resumo Business com cartão cadastrado e ganhe visibilidade sobre clientes, equipe e demandas em tempo real.',
    finalButton: 'Iniciar teste Business',
    securityDescription: 'O Zapt Resumo Business foi desenhado para produtividade com governança. Você controla o que entra, o que é processado e como os dados são usados dentro da operação.',
    accentIcon: BriefcaseBusiness
  }
};

export default function LandingPage({ variant = 'personal' }) {
  const { user } = useAuth();
  const location = useLocation();
  const [billingCycle, setBillingCycle] = useState('annual');
  const [checkoutLoadingPlan, setCheckoutLoadingPlan] = useState(null);
  const leadIdRef = useRef('');
  const sessionIdRef = useRef('');
  const trackedSectionsRef = useRef(new Set());
  const trackedMilestonesRef = useRef(new Set());

  const selectedVariant = variant === 'business' || location.pathname === '/business' ? 'business' : 'personal';
  const content = LANDING_VARIANTS[selectedVariant];

  const formatCurrency = (value) => `R$ ${value.toFixed(2).replace('.', ',')}`;

  const trackLandingEvent = (eventName, options = {}) => {
    if (!leadIdRef.current || !sessionIdRef.current) return;

    const env = detectClientEnvironment();
    fetch(TRACKING_API, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        event_name: eventName,
        session_id: sessionIdRef.current,
        lead_id: leadIdRef.current,
        path: location.pathname,
        url: window.location.href,
        referrer: document.referrer || '',
        section: options.section || null,
        cta_id: options.ctaId || null,
        viewport_width: env.viewportWidth,
        viewport_height: env.viewportHeight,
        device_type: env.deviceType,
        device_label: env.deviceLabel,
        browser: env.browser,
        os: env.os,
        metadata: {
          variant: selectedVariant,
          ...(options.metadata || {}),
        },
      }),
      keepalive: true,
    }).catch(() => {});
  };

  useEffect(() => {
    try {
      window.localStorage.setItem('zapt_account_segment', selectedVariant);
    } catch {}
    leadIdRef.current = getOrCreateStorageId(`zapt_lead_id_${selectedVariant}`, window.localStorage);
    sessionIdRef.current = getOrCreateStorageId(`zapt_session_id_${selectedVariant}`, window.sessionStorage);
    trackLandingEvent('page_view', { metadata: { variant: selectedVariant } });
  }, [selectedVariant]);

  useEffect(() => {
    const sections = document.querySelectorAll('[data-track-section]');
    if (!sections.length) return undefined;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const sectionName = entry.target.getAttribute('data-track-section');
        if (!sectionName || trackedSectionsRef.current.has(sectionName)) return;
        trackedSectionsRef.current.add(sectionName);
        trackLandingEvent('section_view', { section: sectionName });
      });
    }, { threshold: 0.4 });

    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, [selectedVariant]);

  useEffect(() => {
    const handleScroll = () => {
      const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
      if (maxScroll <= 0) return;
      const ratio = Math.round((window.scrollY / maxScroll) * 100);
      [25, 50, 75, 100].forEach((milestone) => {
        if (ratio >= milestone && !trackedMilestonesRef.current.has(`${selectedVariant}-${milestone}`)) {
          trackedMilestonesRef.current.add(`${selectedVariant}-${milestone}`);
          trackLandingEvent('scroll_depth', {
            metadata: { milestone },
          });
        }
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [selectedVariant]);

  const plans = [
    {
      id: 'starter',
      name: 'Starter',
      price: billingCycle === 'annual'
        ? formatCurrency(selectedVariant === 'business' ? 9.97 : 5.97)
        : formatCurrency(selectedVariant === 'business' ? 12.90 : 7.97),
      period: '/mês',
      description: content.plans.starterDescription,
      billingNote: billingCycle === 'annual'
        ? `Pagamento anual de ${formatCurrency((selectedVariant === 'business' ? 9.97 : 5.97) * 12)} após 7 dias de teste`
        : 'Cobrança mensal após 7 dias de teste',
      savingsNote: billingCycle === 'annual'
        ? `Economia de ${formatCurrency(((selectedVariant === 'business' ? 12.90 : 7.97) * 12) - ((selectedVariant === 'business' ? 9.97 : 5.97) * 12))} no período`
        : '',
      features: [
        '7 dias de teste com cartão',
        '1 workspace',
        'Até 2 grupos importados',
        'Até 120 resumos por mês',
        'Até 2 resumos por dia em cada grupo',
        'Até 3 perguntas por dia para a IA do grupo',
        selectedVariant === 'business' ? 'Validação inicial da operação' : 'Sugestões básicas de resposta',
      ],
      cta: 'Iniciar teste de 7 dias',
      popular: false
    },
    {
      id: 'pro',
      name: 'Pro',
      price: billingCycle === 'annual'
        ? formatCurrency(selectedVariant === 'business' ? 12.90 : 6.97)
        : formatCurrency(selectedVariant === 'business' ? 19.90 : 9.97),
      period: '/mês',
      description: content.plans.proDescription,
      billingNote: billingCycle === 'annual'
        ? `Pagamento anual de ${formatCurrency((selectedVariant === 'business' ? 12.90 : 6.97) * 12)} após 7 dias de teste`
        : 'Cobrança mensal após 7 dias de teste',
      savingsNote: billingCycle === 'annual'
        ? `Economia de ${formatCurrency(((selectedVariant === 'business' ? 19.90 : 9.97) * 12) - ((selectedVariant === 'business' ? 12.90 : 6.97) * 12))} no período`
        : '',
      features: [
        '7 dias de teste com cartão',
        'Grupos ilimitados',
        'Resumos ilimitados',
        'Perguntas ilimitadas para a IA',
        selectedVariant === 'business' ? 'Leitura de contexto profissional' : 'Sugestões com IA avançada',
        selectedVariant === 'business' ? 'Priorização de demandas e follow-up' : 'Histórico de 90 dias',
        'Fluxo de importação recorrente',
        'Suporte prioritário',
      ],
      cta: 'Iniciar teste de 7 dias',
      popular: true
    },
    {
      id: 'business',
      name: 'Business',
      displayName: 'Premium',
      price: billingCycle === 'annual'
        ? formatCurrency(selectedVariant === 'business' ? 49.90 : 29.97)
        : formatCurrency(selectedVariant === 'business' ? 69.90 : 39.97),
      period: '/mês',
      description: content.plans.businessDescription,
      billingNote: billingCycle === 'annual'
        ? `Pagamento anual de ${formatCurrency((selectedVariant === 'business' ? 49.90 : 29.97) * 12)} após 7 dias de teste`
        : 'Cobrança mensal após 7 dias de teste',
      savingsNote: billingCycle === 'annual'
        ? `Economia de ${formatCurrency(((selectedVariant === 'business' ? 69.90 : 39.97) * 12) - ((selectedVariant === 'business' ? 49.90 : 29.97) * 12))} no período`
        : '',
      features: [
        '7 dias de teste com cartão',
        '5 membros inclusos',
        'Resumos ilimitados',
        'Perguntas ilimitadas para a IA',
        'Workspaces múltiplos',
        selectedVariant === 'business' ? 'Leitura de oportunidades e risco comercial' : 'Fluxos assistidos para operação',
        'Relatórios avançados',
        'Roadmap de app e share extension',
        'Suporte dedicado',
      ],
      cta: 'Iniciar teste de 7 dias',
      popular: false
    }
  ];

  const handleGetStarted = (ctaId = null) => {
    trackLandingEvent('cta_click', { ctaId: ctaId || (user ? 'go-to-app' : 'start-now') });
    document.getElementById('planos')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handlePlanCheckout = async (planId) => {
    trackLandingEvent('cta_click', {
      ctaId: `pricing-${selectedVariant}-${planId}-${billingCycle}`,
      section: 'planos',
      metadata: { plan: planId, billingCycle, variant: selectedVariant },
    });

    try {
      setCheckoutLoadingPlan(planId);
      const endpoint = user ? `${API}/billing/checkout-session` : `${API}/public/billing/checkout-session`;
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          plan: planId,
          billing_cycle: billingCycle,
          ...(user ? {} : { account_segment: selectedVariant }),
          success_url: user
            ? `${window.location.origin}/checkout/success?session_id={CHECKOUT_SESSION_ID}&source=landing`
            : `${window.location.origin}/checkout/success?session_id={CHECKOUT_SESSION_ID}`,
          cancel_url: user
            ? `${window.location.origin}/app/billing?checkout=cancelled`
            : `${window.location.origin}${selectedVariant === 'business' ? '/business' : '/pessoal'}#planos`,
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || 'Não foi possível iniciar o checkout.');
      }
      if (data.checkout_url) {
        await openExternalUrl(data.checkout_url);
        return;
      }
      throw new Error('Checkout sem URL de redirecionamento.');
    } catch (error) {
      toast.error(error.message || 'Erro ao iniciar checkout');
    } finally {
      setCheckoutLoadingPlan(null);
    }
  };

  return (
    <div className="min-h-screen bg-white" style={{ paddingBottom: 'max(1rem, var(--safe-area-bottom))' }}>
      <header
        className="fixed top-0 left-0 right-0 z-50 glass border-b border-zinc-200/50"
        style={{ paddingTop: 'var(--safe-area-top)' }}
      >
        <div className="max-w-7xl mx-auto px-3 md:px-6 py-4 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-sky-500 to-violet-600 flex items-center justify-center flex-shrink-0">
              <BrandIcon className="w-4 h-4 text-white" />
            </div>
            <div className="min-w-0 flex-1">
              <span className="font-heading text-[15px] md:text-xl font-semibold text-zinc-900 block leading-tight truncate">Zapt Resumo</span>
              <div className="flex items-center gap-2 leading-tight">
                <span className="text-[9px] md:text-[10px] font-semibold tracking-[0.14em] text-sky-700 block">
                  {selectedVariant === 'business' ? 'BUSINESS' : 'PESSOAL'}
                </span>
                <span className="hidden sm:inline text-[10px] text-zinc-400 font-medium">by Openova</span>
              </div>
            </div>
          </div>

          <nav className="hidden md:flex items-center gap-8">
            <a href="#como-funciona" className="text-sm text-zinc-600 hover:text-zinc-900 transition-colors">Como Funciona</a>
            <a href="#beneficios" className="text-sm text-zinc-600 hover:text-zinc-900 transition-colors">Benefícios</a>
            <a href="#planos" className="text-sm text-zinc-600 hover:text-zinc-900 transition-colors">Planos</a>
            <a href="/security" className="text-sm text-zinc-600 hover:text-zinc-900 transition-colors">Segurança</a>
          </nav>

          <div className="flex items-center gap-2 md:gap-3 flex-shrink-0">
            <a
              href={content.switchHref}
              className="hidden md:inline-flex rounded-full border border-sky-200 bg-sky-50 px-4 py-2 text-sm font-medium text-sky-700 transition-colors hover:bg-sky-100"
            >
              {content.switchLabel}
            </a>
            {user ? (
              <Button
                onClick={() => handleGetStarted('header-app')}
                className="rounded-full px-6 bg-sky-600 hover:bg-violet-700 text-white"
              >
                Ir para os Planos
              </Button>
            ) : (
              <>
                <Button
                  variant="ghost"
                  onClick={() => {
                    trackLandingEvent('cta_click', { ctaId: 'header-login' });
                    window.location.href = '/login';
                  }}
                  className="text-zinc-600 hover:text-zinc-900 px-3 md:px-4"
                >
                  Entrar
                </Button>
                <Button
                  onClick={() => handleGetStarted('header-cta')}
                  className="rounded-full px-4 md:px-6 bg-sky-600 hover:bg-violet-700 text-white"
                >
                  Começar Agora
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      <section
        className="pb-20 relative overflow-hidden"
        id="hero"
        data-track-section="hero"
        style={{ paddingTop: 'calc(8rem + var(--safe-area-top))' }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-sky-50/80 via-white to-white" />
        <div className="absolute inset-0 hero-grid-pattern opacity-30" />
        <FloatingParticles accentIcon={content.accentIcon} />

        <div className="max-w-7xl mx-auto px-4 md:px-6 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center gap-2 bg-sky-100 text-sky-700 px-4 py-2 rounded-full text-sm font-medium mb-8 animate-pulse-subtle">
                <BrandIcon className="w-4 h-4" />
                {content.heroBadge}
              </div>

              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-zinc-900 mb-6 tracking-tight">
                {content.heroTitleStart}{' '}
                <span className="gradient-text relative">
                  {content.heroTitleHighlight}
                  <svg className="absolute -bottom-2 left-0 w-full" viewBox="0 0 200 8" fill="none">
                    <path d="M1 5.5C47 2 153 2 199 5.5" stroke="#0EA5E9" strokeWidth="3" strokeLinecap="round" className="animate-draw-line"/>
                  </svg>
                </span>
              </h1>

              <p className="text-lg text-zinc-600 mb-10 max-w-xl leading-relaxed">
                {content.heroDescription}
                <strong className="text-zinc-800"> {content.heroStrong}</strong>
              </p>
              <div className="mb-8 inline-flex rounded-full border border-emerald-200 bg-emerald-50 px-4 py-2 text-left">
                <span className="inline-flex rounded-full bg-emerald-600 px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.12em] text-white">
                  Teste por 7 dias grátis!
                </span>
              </div>
              <p className="text-sm text-zinc-500 mb-8 max-w-2xl leading-relaxed">
                O Zapt Resumo é uma ferramenta independente da Openova e não é afiliado, associado ou endossado pelo WhatsApp LLC.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Button
                  onClick={() => handleGetStarted('hero-cta')}
                  size="lg"
                  className="rounded-full px-8 py-6 text-base bg-sky-600 hover:bg-violet-700 shadow-lg shadow-sky-500/25 hover:shadow-violet-500/30 transition-all duration-300 hover:scale-105"
                >
                  <BrandIcon className="w-5 h-5 mr-2" />
                  {selectedVariant === 'business' ? 'Conhecer versão Business' : 'Testar Agora'}
                </Button>
              </div>

              <div className="flex items-center gap-8 mt-10 justify-center lg:justify-start">
                {content.stats.map((stat, index) => (
                  <div key={stat.label} className="flex items-center gap-8">
                    {index > 0 ? <div className="w-px h-10 bg-zinc-200" /> : null}
                    <div className="text-center">
                      <p className="text-2xl font-bold text-zinc-900">
                        <AnimatedCounter end={stat.end} suffix={stat.suffix} />
                      </p>
                      <p className="text-xs text-zinc-500">{stat.label}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="hidden lg:block">
              <InteractiveDemo
                title={content.demoTitle}
                subtitle={content.demoSubtitle}
                tabs={content.demoTabs}
                content={content.demoContent}
              />
            </div>
          </div>
        </div>
      </section>

      <section id="como-funciona" data-track-section="como-funciona" className="py-24 bg-zinc-50/50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl font-bold text-zinc-900 mb-4">Como Funciona</h2>
            <p className="text-zinc-600 max-w-xl mx-auto">
              {selectedVariant === 'business'
                ? 'Uma camada de IA para transformar comunicação dispersa em produtividade operacional.'
                : 'Um fluxo prático para transformar conversas caóticas em clareza e ação.'}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {content.howItWorks.map((item, index) => (
              <div key={index} className="feature-card text-center">
                <div className="flex items-center justify-center gap-4 mb-6">
                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-sky-100 flex-shrink-0">
                    <item.icon className="w-6 h-6 text-sky-600" />
                  </div>
                  <span className="text-sm font-mono tracking-[0.2em] text-sky-600 font-medium leading-none">
                    PASSO {item.step}
                  </span>
                </div>
                <h3 className="font-heading text-xl font-semibold text-zinc-900 mt-2 mb-3">{item.title}</h3>
                <p className="text-zinc-600 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="beneficios" data-track-section="beneficios" className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-20 right-20 w-72 h-72 bg-emerald-500 rounded-full blur-3xl" />
          <div className="absolute bottom-20 left-20 w-96 h-96 bg-emerald-400 rounded-full blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl font-bold text-zinc-900 mb-4">
              {content.benefitsTitle}
            </h2>
            <p className="text-zinc-600 max-w-xl mx-auto">
              {content.benefitsDescription}
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {content.benefits.map((benefit, index) => (
              <div
                key={index}
                className="group p-6 bg-white rounded-2xl border border-zinc-200 hover:border-emerald-300 hover:shadow-lg hover:shadow-emerald-500/10 transition-all duration-300 hover:-translate-y-1"
              >
                <div className={`w-12 h-12 rounded-xl bg-${benefit.color}-100 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                  <benefit.icon className={`w-6 h-6 text-${benefit.color}-600`} />
                </div>
                <h4 className="font-heading font-semibold text-zinc-900 mb-2">{benefit.title}</h4>
                <p className="text-sm text-zinc-600">{benefit.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-zinc-900 text-white" data-track-section="seguranca">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center">
            <Shield className="w-12 h-12 text-emerald-400 mx-auto mb-6" />
            <h2 className="font-heading text-3xl font-bold mb-6">Segurança e LGPD</h2>
            <p className="text-zinc-400 mb-8 leading-relaxed">
              {content.securityDescription}
            </p>
            <Button
              variant="outline"
              className="rounded-full border-zinc-700 text-white hover:bg-zinc-800"
              onClick={() => {
                trackLandingEvent('cta_click', { ctaId: 'security-page' });
                window.location.href = '/security';
              }}
            >
              Saiba Mais sobre Privacidade
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </section>

      <section id="planos" data-track-section="planos" className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl font-bold text-zinc-900 mb-4">Planos</h2>
            <p className="text-zinc-600">
              {selectedVariant === 'business'
                ? 'Comece validando sua operação e evolua para um plano de time.'
                : 'Escolha o plano ideal para suas necessidades.'}
            </p>
          </div>

          <div className="flex justify-center mb-10">
            <div className="inline-flex items-center rounded-full border border-sky-100 bg-sky-50 p-1">
              <button
                type="button"
                onClick={() => {
                  setBillingCycle('monthly');
                  trackLandingEvent('billing_cycle_toggle', {
                    section: 'planos',
                    metadata: { cycle: 'monthly' },
                  });
                }}
                className={`rounded-full px-5 py-2 text-sm font-medium transition-colors ${
                  billingCycle === 'monthly' ? 'bg-white text-zinc-900 shadow-sm' : 'text-sky-700'
                }`}
              >
                Mensal
              </button>
              <button
                type="button"
                onClick={() => {
                  setBillingCycle('annual');
                  trackLandingEvent('billing_cycle_toggle', {
                    section: 'planos',
                    metadata: { cycle: 'annual' },
                  });
                }}
                className={`rounded-full px-5 py-2 text-sm font-medium transition-colors ${
                  billingCycle === 'annual' ? 'bg-sky-600 text-white shadow-sm' : 'text-sky-700'
                }`}
              >
                Anual
              </button>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {plans.map((plan, index) => (
              <div
                key={index}
                className={`relative p-8 rounded-2xl border ${plan.popular ? 'pricing-popular bg-white shadow-xl' : 'bg-white border-zinc-200'}`}
              >
                <div className="absolute right-4 top-4">
                  <span className="inline-flex rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-700">
                    7 dias grátis
                  </span>
                </div>
                <h3 className="font-heading text-xl font-semibold text-zinc-900">{plan.displayName || plan.name}</h3>
                <p className="text-sm text-zinc-500 mb-4">{plan.description}</p>
                <div className="mb-6">
                  <span className="text-4xl font-bold text-zinc-900">{plan.price}</span>
                  <span className="text-zinc-500">{plan.period}</span>
                  {plan.billingNote ? <p className="mt-2 text-sm text-zinc-500">{plan.billingNote}</p> : null}
                  {plan.savingsNote ? <p className="mt-1 text-sm font-medium text-emerald-600">{plan.savingsNote}</p> : null}
                </div>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-zinc-600">
                      <Check className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Button
                  onClick={() => {
                    handlePlanCheckout(plan.id);
                  }}
                  className={`w-full rounded-full ${plan.popular ? 'bg-sky-600 hover:bg-violet-700' : 'bg-white border border-zinc-300 text-zinc-900 hover:bg-zinc-50'}`}
                  variant={plan.popular ? 'default' : 'outline'}
                  disabled={checkoutLoadingPlan === plan.id}
                >
                  {checkoutLoadingPlan === plan.id ? 'Redirecionando...' : plan.cta}
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-zinc-50/50" data-track-section="faq">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl font-bold text-zinc-900 mb-4">Perguntas Frequentes</h2>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {content.faq.map((item, index) => (
              <AccordionItem key={index} value={`item-${index}`} className="bg-white rounded-xl border border-zinc-200 px-6">
                <AccordionTrigger
                  className="text-left font-heading font-medium text-zinc-900 hover:no-underline py-5"
                  onClick={() => trackLandingEvent('faq_open', {
                    section: 'faq',
                    metadata: { question: item.q },
                  })}
                >
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="text-zinc-600 pb-5">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <section className="py-24" data-track-section="final-cta">
        <div className="max-w-7xl mx-auto px-6">
          <div className="bg-gradient-to-br from-sky-600 to-violet-700 rounded-3xl p-12 text-center text-white">
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              {content.finalTitle}
            </h2>
            <p className="text-emerald-100 mb-8 max-w-xl mx-auto">
              {content.finalDescription}
            </p>
            <Button
              onClick={() => {
                handleGetStarted('final-cta');
              }}
              size="lg"
              className="rounded-full px-8 py-6 text-base bg-white text-sky-700 hover:bg-zinc-100"
            >
              <BrandIcon className="w-5 h-5 mr-2" />
              {content.finalButton}
            </Button>
          </div>
        </div>
      </section>

      <footer className="py-12 border-t border-zinc-200">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-md bg-gradient-to-br from-sky-500 to-violet-600 flex items-center justify-center">
                <BrandIcon className="w-3 h-3 text-white" />
              </div>
              <span className="font-heading font-semibold text-zinc-900">Zapt Resumo</span>
              <span className="text-xs text-zinc-400">by Openova</span>
            </div>

            <div className="flex items-center gap-6 text-sm text-zinc-500">
              <a href={selectedVariant === 'business' ? '/pessoal' : '/business'} className="hover:text-zinc-900 transition-colors">
                {selectedVariant === 'business' ? 'Versão Pessoal' : 'Versão Business'}
              </a>
              <a href="/security" className="hover:text-zinc-900 transition-colors">Privacidade</a>
              <a href="#planos" className="hover:text-zinc-900 transition-colors">Planos</a>
              <span>© 2026 Openova</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
